import React, { useState, useEffect, useContext } from "react";
import isEqual from "lodash.isequal";
import Modal from "common/Modal";
import ActionsContext from "common/ActionsContext";
import SelectInputWithIcon from "common/SelectInputWithIcon";
import { safeStr } from "common/api";
import SelectAutocomplete from "common/SelectAutocomplete";
import { buildCommand } from "./utils";
import { CONST_TREE_RULE } from "./constants";
import BqnContext from "common/BqnContext";

function getDuplicatedRule(rules, rule) {
  return rules.find((ruleX) => {
    const newRule = { ...ruleX };
    delete newRule.id;
    return isEqual(newRule, rule);
  });
}

function getProfilesInfoFns(ifCl, profs, view) {
  const policyCmd = view === "rate" ? "show policy api full" : `show policy ${view} web`;
  return [
    ...profs.map((prof) => {
      return ifCl.run(`show profile ${prof.type}`);
    }),
    ifCl.run(policyCmd),
  ];
}

const doLoad = (ifCl, profs, view, bqn) => {
  return Promise.all(getProfilesInfoFns(ifCl, profs, view)).then((response) => {
    return response.map((respProfile, index) => {
      const [header, ...rows] = respProfile.trim(/\s/).split("\n");
      const headerAttrs = header.trim(/\s/).split(/\s+/);
      const nameId = headerAttrs.findIndex((el) => el === "NAME");
      const policyId = headerAttrs.findIndex((el) => el === "POLICY");
      const sourceId = headerAttrs.findIndex((el) => el === "ASSIGNMENT");
      const bqnId = headerAttrs.findIndex((el) => el === "BQN");
      const names = rows.reduce((accNames, row) => {
        const rowAttrs = row.trim(/\s/).split(/\s+/);
        const name = rowAttrs[nameId];
        const bqnFound = bqnId < 0 ? undefined : parseFloat(rowAttrs[bqnId]);
        if(index === response.length - 1){
          if(view==="rate"){
            const source = rowAttrs[sourceId];
            if (source === "configured" && bqnFound === parseFloat(bqn)) {
              accNames.push(rowAttrs[policyId]);
            }
          } else {
            accNames.push(rowAttrs[nameId]);
          }
        } else {
          accNames.push(name);  
        }

        return accNames;
      }, []);
      return names;
    });
  });
};

const ModalContent = ({
  rule,
  options,
  selection,
  setSelection,
  profs,
  poli,
}) => {
  return (
    <div>
      {[...profs, poli].map(({ titleEdit, attr }) => {
        const optionsProfile = options[attr] || [];
        const { optionsUn, optionAny } = optionsProfile.reduce(
          (acc, option) => {
            const { value } = option;
            if (!acc.set.has(value)) {
              acc.set.add(value);
              if (value === "any") {
                acc.optionAny.push(option);
              } else {
                acc.optionsUn.push(option);
              }
            }
            return acc;
          },
          { optionsUn: [], set: new Set(), optionAny:[] }
        );
        return (
          <div
            className="form-group margin-b-15 margin-t-15"
            key={`profile-${attr}`}
          >
            <label>{titleEdit}:</label>
            <SelectAutocomplete
              title={titleEdit}
              name={attr}
              icon="place"
              selected={selection[attr]}
              onChange={(value) => {
                setSelection({ ...selection, [attr]: value });
              }}
              options={[...optionAny, ...optionsUn.sort()]}
            />
          </div>
        );
      })}
    </div>
  );
};

const RuleModal = ({
  rules,
  optionsProfs,
  initSelection,
  view,
  profs,
  poli,
}) => {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("Add");
  const [options, setOptions] = useState(optionsProfs);
  const [selectedOptions, setSelectedOptions] = useState({...initSelection});
  const [rule, setRule] = useState();
  const [errorFooter, setErrorFooter] = useState("");
  const actions = useContext(ActionsContext);
  const doClose = () => setOpen(false);
  const {
    loginBqnId,
    clusterStatus: { mode },
    bqnId,
  } = useContext(BqnContext);
  const bqnToShow = bqnId === "all" ? loginBqnId : bqnId;

  useEffect(() => {
    return actions.recv("open-rule-modal", function ({ rule, type }) {
      setType(type);

      if (rule) {
        const byRuleSlectedOptions = Object.entries(rule).reduce(
          (acc, [name, value]) => {
            if (acc[name]) {
              acc[name] = value;
            }
            return acc;
          },
          selectedOptions
        );
        setSelectedOptions({...byRuleSlectedOptions});
        setRule(rule);
      }
      setErrorFooter("");
      setOpen(true);
      doLoad(ifCl, profs, view, bqnToShow).then((parsedResponse) => {
        const newOptions = parsedResponse.reduce(
          (acc, value, index) => {
            let attr;
            if (index === profs.length) {
              attr = "poli";
            } else {
              attr = profs[index].attr;
            }
            const optionsNames = value.reduce((accOptions, option) => {
              if (attr === "poli" && option === `${view}-default`) {
                return accOptions;
              } else {
                accOptions.push({ label: option, value: option });
                return accOptions;
              }
            }, []);

            acc[attr] = [...optionsNames, ...acc[attr]];
            return acc;
          },
          { ...optionsProfs }
        );
        setOptions(newOptions);
      });
    });
  }, []);

  if (open === false) {
    return null;
  }

  const duplicate = getDuplicatedRule(rules, selectedOptions);

  const handleApplyRule = (response) => {
    if (response.length === 0) {
      setSelectedOptions({...initSelection});
      actions.send("do-load");
    } else if (response.substring(0, 5) == "%WARN") {
      showModalInfo("Warning:", response);
      actions.send("do-load");
    } else {
      showModalError("Error:", response);
    }
    setOpen(false);
  };

  const setNewRule = (rule) => {
    ifCl
      .run(buildCommand("set", selectedOptions, view))
      .then(handleApplyRule)
      .catch((error) => {
        setErrorFooter(`Error: ${error}`);
      });
  };

  const applyRule = () => {
    if (type === "Edit") {
      if (selectedOptions["poli"] === "any") {
        setErrorFooter("No policy specified");
      } else {
        ifCl
          .run(buildCommand("clear", rule, view))
          .then(() => ifCl.run(buildCommand("set", selectedOptions, view)))
          .then(handleApplyRule)
          .catch((error) => {
            showModalError("Error:", response);
            setOpen(false);
            setNewRule(rule);
          });
      }
    } else {
      if (selectedOptions["poli"] === "any") {
        setErrorFooter("No policy specified");
      } else if (duplicate !== undefined) {
        setErrorFooter("Duplicated rule");
      } else {
        setNewRule(selectedOptions);
      }
    }
  };

  const handleSelectionChange = (options) => {
    setErrorFooter("");
    setSelectedOptions({...options});
    const duplicate = getDuplicatedRule(rules, options);
    if (duplicate !== undefined) {
      setErrorFooter("Duplicated rule");
    }
  };

  return (
    <Modal
      title={`${type} ${view} rule`}
      superIcon={type === "Add" ? "add_circle" : "edit"}
      large={false}
      content={() => (
        <ModalContent
          rule={rule}
          options={options}
          selection={{...selectedOptions}}
          setSelection={handleSelectionChange}
          profs={profs}
          poli={poli}
        />
      )}
      applyLabel="OK"
      onApply={applyRule}
      applyDisabled={duplicate !== undefined}
      closeLabel="CANCEL"
      onClose={() => {
        setSelectedOptions({...initSelection});
        setOpen(false);
      }}
      footerMessage={
        errorFooter !== "" ? (
          <span className="modal-err-msg color-red">
            <h5>{errorFooter}</h5>
          </span>
        ) : null
      }
    />
  );
};

export default RuleModal;
